import { useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { parseISO } from "date-fns";

import { ErrorAlert, SuccessAlert } from "@shared/ui/alerts";
import { InputController, InputDatePickerController } from "@shared/ui/form";
import { useActivities } from "../hooks/useActivities";
import { useUpdateMonthlyHours } from "../hooks/useUpdateMonthlyHours";
import { buildMonthlyHourSchema } from "../schema/monthlyHourSchema";
import { EntitySelect } from "./EntitySelect";

import type { MonthlySummaryHours } from "../api/timeEntryApi";

import type { TFunction } from "i18next";

/**
 * Modelo completo del form: además de `hours` (el único campo que valida el schema yup,
 * ver schema/monthlyHourSchema.ts), el resto de campos se precargan vía `setValue` desde
 * el registro seleccionado en la tabla (`MonthlySummaryHours`) y no se editan a mano.
 */
interface MonthlyHourFormValues {
  id?: number;
  projectId?: number;
  projectName?: string;
  activityId?: number;
  hours?: number;
  comment?: string;
  spentOn?: Date;
}

export interface MonthlyHourDialogProps {
  handleClose: () => void;
  open: boolean;
  notLoading: () => void;
  loading: () => void;
  t: TFunction;
  data?: MonthlySummaryHours;
  reloadProjectData: () => void;
}

export const MonthlyHourDialog = ({
  handleClose,
  open,
  notLoading,
  loading,
  t,
  data,
  reloadProjectData,
}: MonthlyHourDialogProps) => {
  const { control, handleSubmit, setValue, formState: { errors } } = useForm<MonthlyHourFormValues>({
    resolver: yupResolver(buildMonthlyHourSchema(t)) as never,
  });

  // Carga actividades al abrir el dialog.
  const { data: activityList } = useActivities(open);

  // Precarga los valores del registro a editar cuando cambia el prop data.
  useEffect(() => {
    if (data) {
      setValue("id", data.id);
      setValue("projectId", data.projectId);
      setValue("projectName", data.projectName);
      setValue("activityId", data.activityId);
      setValue("hours", data.hours);
      setValue("comment", data.comment);
      // `data.spentOn` viene del contrato como opcional; comportamiento preexistente sin
      // guard (no se agrega uno nuevo acá, ver #2572 para el resto de accesos sin guard).
      setValue("spentOn", parseISO(data.spentOn!));
    }
  }, [data]);

  const updateMonthlyHours = useUpdateMonthlyHours();

  const onRegister = (value: MonthlyHourFormValues) => {
    handleClose();
    loading();
    // El payload envía `spentOn` como Date real (no como string ISO): comportamiento
    // preexistente, comprobado por MonthlyHourDialog.test.tsx (`toBeInstanceOf(Date)`).
    // El contrato generado tipa `spentOn` como string; se documenta el cast en vez de
    // serializar la fecha, lo que cambiaría el payload real.
    updateMonthlyHours.mutate(value as unknown as MonthlySummaryHours, {
      onSuccess: () => {
        notLoading();
        SuccessAlert(t("loadHours"), t("doneSuccessfully", { a: "a" })).then(
          () => reloadProjectData()
        );
      },
      onError: (error) => {
        const errorData = error.response?.data;
        notLoading();
        handleClose();
        ErrorAlert(t("errorTitle"), errorData?.message ?? t("internalError")).then();
      },
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(onRegister)}>
        <DialogTitle>{t("updateTimeStamp")}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid id="hours-field" size={12}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputController
                  control={control}
                  defaultValue={8}
                  label={t("project")}
                  name="projectName"
                  disabled={true}
                />
                {/* Bug preexistente, anterior a la migración: este bloque y el de "comment"
                    más abajo leen errors.hours en vez
                    de un error propio — el schema solo valida "hours", así que en la práctica es
                    inofensivo (siempre false salvo que errors.hours esté seteado, en cuyo caso
                    duplicaría el mensaje bajo el campo equivocado). No es un fix aislado limpio
                    (no hay validación real de "projectName"/"comment" que restaurar); se preserva
                    el comportamiento. */}
                {errors.hours && (
                  <span style={{ fontSize: 12, color: "red" }}>
                    {errors.hours.message}
                  </span>
                )}
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <EntitySelect
                  control={control}
                  defaultValue=""
                  label={t("activities")}
                  name="activityId"
                  listValue={activityList}
                />
                {errors.activityId && (
                  <span style={{ fontSize: 12, color: "red" }}>
                    {errors.activityId.message}
                  </span>
                )}
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputDatePickerController
                  control={control}
                  defaultValue={new Date()}
                  openTo="month"
                  name="spentOn"
                  label={t("date")}
                  views={["year", "month", "day"]}
                />
                {errors.spentOn && (
                  <span style={{ fontSize: 12, color: "red" }}>
                    {errors.spentOn.message}
                  </span>
                )}
              </FormControl>
            </Grid>
            <Grid id="hours-field" size={12}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputController
                  control={control}
                  defaultValue={8}
                  label={t("hours")}
                  name="hours"
                  type="number"
                />
                {errors.hours && (
                  <span style={{ fontSize: 12, color: "red" }}>
                    {errors.hours.message}
                  </span>
                )}
              </FormControl>
            </Grid>
            <Grid id="hours-field" size={12}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputController
                  control={control}
                  defaultValue={8}
                  label={t("description")}
                  name="comment"
                />
                {errors.hours && (
                  <span style={{ fontSize: 12, color: "red" }}>
                    {errors.hours.message}
                  </span>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t("cancel")}</Button>
          <Button type="submit">{t("update")}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
